import { isValidKey, KEY_PATTERN, NOTE_MAX, parseDotenv } from '@fleequid-secrets/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

export interface VariableDraft {
    key: string;
    value: string;
    note: string;
}

const ONE_ENTRY = /^\s*(?:export\s+)?[A-Za-z_][A-Za-z0-9_]*\s*=/;

// A variable being written: its key, its value (any length, any lines), a note. Pasting `KEY=value` into the key
// splits it; pasting a whole .env hands it to the import. ⌘↵ saves, Esc gives up.
export function VariableEditor(props: {
    mode: 'new' | 'edit';
    initial: VariableDraft;
    loading: boolean;
    busy: boolean;
    error: string | null;
    onSave: (draft: VariableDraft) => void;
    onCancel: () => void;
    onPasteFile: (text: string) => void;
}): ReactNode {
    const [draft, setDraft] = useState<VariableDraft>(props.initial);
    const keyField = useRef<HTMLInputElement>(null);
    const valueField = useRef<HTMLTextAreaElement>(null);
    const [touched, setTouched] = useState(false);

    // An edit opens before its value arrives (it is revealed for editing): the board mounts a new editor once it does,
    // so the draft starts from the whole variable.
    useEffect(() => {
        (props.mode === 'new' ? keyField : valueField).current?.focus();
    }, [props.mode, props.loading]);

    const keyProblem = draft.key && !isValidKey(draft.key) ? 'Letters, digits and _, not starting with a digit' : null;
    const lines = Math.min(12, Math.max(1, draft.value.split('\n').length));
    const save = (): void => {
        setTouched(true);
        if (draft.key && !keyProblem && !props.busy && !props.loading) {
            props.onSave({ ...draft, note: draft.note.trim() });
        }
    };

    return (
        <div
            className="veditor"
            onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    save();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    props.onCancel();
                }
            }}
        >
            <label>
                <span className="micro">Key</span>
                <input
                    ref={keyField}
                    className={`field mono ${touched && (keyProblem || !draft.key) ? 'invalid' : ''}`}
                    value={draft.key}
                    placeholder="DATABASE_URL"
                    spellCheck={false}
                    autoComplete="off"
                    maxLength={128}
                    pattern={KEY_PATTERN.source}
                    onChange={(event) => setDraft({ ...draft, key: event.target.value.trim() })}
                    onPaste={(event) => {
                        const text = event.clipboardData.getData('text');
                        if (text.trim().includes('\n')) {
                            event.preventDefault();
                            props.onPasteFile(text);
                        } else if (ONE_ENTRY.test(text)) {
                            const entry = parseDotenv(text).entries[0];
                            if (entry) {
                                event.preventDefault();
                                setDraft({ ...draft, key: entry.key, value: entry.value });
                                valueField.current?.focus();
                            }
                        }
                    }}
                />
            </label>
            <label>
                <span className="micro">{props.loading ? 'Value · opening…' : 'Value'}</span>
                <textarea
                    ref={valueField}
                    className="field"
                    rows={lines}
                    value={draft.value}
                    disabled={props.loading}
                    placeholder={props.loading ? '' : 'The value, as the app reads it: quotes are not needed'}
                    spellCheck={false}
                    autoComplete="off"
                    onChange={(event) => setDraft({ ...draft, value: event.target.value })}
                />
            </label>
            <label className="wide">
                <span className="micro">Note · optional, not encrypted: never a secret here</span>
                <input
                    className="field"
                    value={draft.note}
                    maxLength={NOTE_MAX}
                    placeholder="What it is for, where it comes from"
                    onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                />
            </label>
            {(props.error || (touched && keyProblem)) && <div className="form-error">{props.error ?? keyProblem}</div>}
            <div className="foot">
                <span className="hint">
                    <span className="kbd">⌘↵</span> save · <span className="kbd">Esc</span> cancel
                    {props.mode === 'new' && ' · paste KEY=value into the key to fill both'}
                </span>
                <span className="spacer" />
                <button className="act" onClick={props.onCancel}>
                    Cancel
                </button>
                <button className="act primary" onClick={save} disabled={props.busy || props.loading || !draft.key || Boolean(keyProblem)}>
                    <Icon name="check" size={15} />
                    {props.busy ? 'Saving…' : props.mode === 'new' ? 'Add' : 'Save'}
                </button>
            </div>
        </div>
    );
}
